import { useEffect, useMemo, useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Check, Loader2, Plus, X } from 'lucide-react'
import type { ProcessStepConnection } from '@/api/processStepsApi'
import { useUpsertProcessStep } from '@/hooks/useProcessSteps'
import {
  SCHEDULE_DAYS,
  STAFFING_ROLE_SUGGESTIONS,
  type ScheduleDay,
  type StaffingDraft,
  emptyStaffingDraft,
  isCustomRole,
  isIncomplete,
  matchSuggestion,
  serializeStaffing,
  toStaffingDraft,
  toStaffingPayload,
  totalPersonHours,
} from '@/lib/processStaffing'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

/** Sentinel for the free-text escape in the role dropdown. */
const OTHER_ROLE = '__other__'

/**
 * Role picker: the starter qualifications, plus a way out.
 *
 * A dropdown alone would be wrong while the staffing master (P5a) does not
 * exist — every site names its qualifications differently, and a fixed list
 * would block authoring rather than tidy it. "Other…" swaps the row to a text
 * input, still writing to the same `roleLabel`, so nothing is lost when P5a
 * later replaces this with a real catalog.
 */
function RolePicker({
  value,
  custom,
  onChange,
  onCustomChange,
  rowLabel,
}: {
  value: string
  custom: boolean
  onChange: (role: string) => void
  onCustomChange: (custom: boolean) => void
  rowLabel: string
}) {
  if (custom) {
    return (
      <div className='flex items-center gap-1.5'>
        <Input
          autoFocus
          aria-label={`Role for ${rowLabel}`}
          placeholder='e.g. Cryopreservation Lead'
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className='h-8 text-sm'
        />
        <button
          type='button'
          aria-label={`Choose from the list instead for ${rowLabel}`}
          title='Choose from the list instead'
          onClick={() => {
            onCustomChange(false)
            onChange('')
          }}
          className='shrink-0 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline'
        >
          List
        </button>
      </div>
    )
  }

  return (
    <Select
      value={matchSuggestion(value) ?? ''}
      onValueChange={(v) => {
        if (v === OTHER_ROLE) {
          onCustomChange(true)
          onChange('')
          return
        }
        onChange(v)
      }}
    >
      <SelectTrigger className='h-8 text-sm' aria-label={`Role for ${rowLabel}`}>
        <SelectValue placeholder='Select a role…' />
      </SelectTrigger>
      <SelectContent>
        {STAFFING_ROLE_SUGGESTIONS.map((role) => (
          <SelectItem key={role} value={role}>
            {role}
          </SelectItem>
        ))}
        <SelectItem value={OTHER_ROLE}>Other…</SelectItem>
      </SelectContent>
    </Select>
  )
}

const DAY_LABELS: Record<ScheduleDay, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
}

/**
 * A compact numeric cell input.
 *
 * `ml-auto` is load-bearing, not spacing. `Input` renders `flex w-full`, so
 * narrowing it to `w-20` leaves a BLOCK-level box that `text-right` on the
 * parent cell cannot move — the header sat right while every input sat hard
 * left. Pushing the box itself right is what lines the column up.
 */
function CellInput({
  label,
  value,
  onChange,
  min = 0,
  step,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  min?: number
  step?: string
}) {
  return (
    <Input
      type='number'
      inputMode='decimal'
      aria-label={label}
      min={min}
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className='ml-auto h-8 w-20 text-right text-sm'
    />
  )
}

/**
 * Mon–Sun toggles for one staffing line.
 *
 * Buttons rather than checkboxes: seven of them sit in a table cell, and a
 * checkbox plus visible label per day is far wider than the column allows.
 * `aria-pressed` is what keeps that accessible — a toggle button reads as one.
 */
function ScheduleDaysPicker({
  value,
  onChange,
  rowLabel,
}: {
  value: ScheduleDay[]
  onChange: (days: ScheduleDay[]) => void
  rowLabel: string
}) {
  const selected = new Set(value)
  const toggle = (day: ScheduleDay) => {
    // Rebuilt from SCHEDULE_DAYS rather than pushed onto, so the stored order is
    // always Mon–Sun regardless of the order someone clicked them in.
    const next = new Set(selected)
    if (next.has(day)) next.delete(day)
    else next.add(day)
    onChange(SCHEDULE_DAYS.filter((d) => next.has(d)))
  }

  return (
    <div className='flex flex-wrap gap-1'>
      {SCHEDULE_DAYS.map((day) => {
        const on = selected.has(day)
        return (
          <button
            key={day}
            type='button'
            aria-pressed={on}
            aria-label={`${DAY_LABELS[day]} for ${rowLabel}`}
            onClick={() => toggle(day)}
            className={cn(
              'h-7 rounded-md border px-2 text-[11px] font-semibold transition-colors',
              on
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-700',
            )}
          >
            {DAY_LABELS[day]}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Process → Staffing: who is needed for this process field, and for how long.
 *
 * ROLE IS FREE TEXT, deliberately and temporarily. The staffing master-data
 * model (P5a) does not exist yet — `StaffingLine.staffingRoleId` is a soft ref
 * the schema documents as "the StaffingRole table does not exist yet; P5a
 * hardens this to an FK", with `roleLabel` alongside as the denormalized display
 * name. Authoring into `roleLabel` is what lets this tab ship before that
 * catalog does; P5a swaps this input for a picker and backfills the id. Any
 * `staffingRoleId` already on a row is preserved untouched on save, so nothing
 * that has been linked gets unlinked by editing hours here.
 */
// Per-user column layout. The role is locked (it identifies the line) and so is
// the remove control. See src/components/table/index.ts.
const STAFFING_TABLE_ID = 'process-staffing'

const STAFFING_COLUMNS: TableColumnDef[] = [
  { id: 'role', header: 'Qualification / role', locked: true },
  { id: 'prep', header: 'Prep' },
  { id: 'setup', header: 'Setup' },
  { id: 'run', header: 'Run' },
  { id: 'unload', header: 'Unload' },
  { id: 'resources', header: '# res' },
  { id: 'scheduleDays', header: 'Schedule days' },
  { id: 'actions', header: 'Remove', locked: true },
]

const STAFFING_HEAD_CLASSES: Record<string, string | undefined> = {
  role: 'min-w-[200px]',
  prep: 'text-right',
  setup: 'text-right',
  run: 'text-right',
  unload: 'text-right',
  resources: 'text-right',
  scheduleDays: 'min-w-[230px]',
  actions: 'w-10',
}

export function StaffingRequirementTab({
  fieldId,
  connection,
}: {
  fieldId: string
  connection: ProcessStepConnection | null
}) {
  const cols = useTableColumns(STAFFING_TABLE_ID, STAFFING_COLUMNS)
  const upsert = useUpsertProcessStep()

  const [rows, setRows] = useState<StaffingDraft[]>([])
  const [savedAt, setSavedAt] = useState<number | null>(null)
  /**
   * Rows the author put into free-text mode by choosing "Other…".
   *
   * Held separately because it is not part of the saved shape — a row whose
   * label already fails `isCustomRole` renders as free text without needing an
   * entry here. This set only covers the in-between state: "Other…" picked, but
   * nothing typed yet, where the value is empty and would otherwise snap back
   * to the dropdown mid-edit.
   */
  const [customRows, setCustomRows] = useState<Set<number>>(new Set())

  const baseline = useMemo(
    () => (connection?.staffingLines ?? []).map(toStaffingDraft),
    [connection],
  )

  useEffect(() => {
    setRows(baseline)
    setSavedAt(null)
    setCustomRows(new Set())
  }, [baseline, fieldId])

  const dirty = serializeStaffing(rows) !== serializeStaffing(baseline)
  const incompleteCount = useMemo(() => rows.filter(isIncomplete).length, [rows])
  const personHours = totalPersonHours(rows)

  const addRow = () => {
    setRows((prev) => [...prev, emptyStaffingDraft()])
    setSavedAt(null)
  }

  const patchRow = (index: number, patch: Partial<StaffingDraft>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)))
    setSavedAt(null)
  }

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index))
    // Indices shift when a row is removed, so the set is rebuilt rather than
    // just having `index` deleted — otherwise every free-text row below the
    // removed one would snap back to the dropdown and lose what was typed.
    setCustomRows((prev) => {
      const next = new Set<number>()
      prev.forEach((i) => {
        if (i < index) next.add(i)
        else if (i > index) next.add(i - 1)
      })
      return next
    })
    setSavedAt(null)
  }

  const setCustomFor = (index: number, custom: boolean) =>
    setCustomRows((prev) => {
      const next = new Set(prev)
      if (custom) next.add(index)
      else next.delete(index)
      return next
    })

  const save = () => {
    // Staffing only — inventory and equipment are omitted, and the endpoint
    // keeps what it is not sent.
    upsert.mutate(
      { fieldId, payload: { staffingLines: toStaffingPayload(rows) } },
      { onSuccess: () => setSavedAt(Date.now()) },
    )
  }

  return (
    <div className='space-y-4 px-5 py-5'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <Button variant='outline' onClick={addRow}>
            <Plus className='mr-1.5 h-4 w-4' />
            Add staffing line
          </Button>
          {rows.length > 0 && (
            <span className='text-xs text-muted-foreground'>
              {rows.length} {rows.length === 1 ? 'line' : 'lines'} · {personHours} person-hours/run
            </span>
          )}
        </div>
        <div className='flex items-center gap-3'>
          {savedAt && !dirty && (
            <span className='flex items-center gap-1 text-xs text-green-600'>
              <Check className='h-3.5 w-3.5' /> Saved
            </span>
          )}
          <Button onClick={save} disabled={!dirty || upsert.isPending || incompleteCount > 0}>
            {upsert.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Save
          </Button>
        </div>
      </div>

      {incompleteCount > 0 && (
        <p className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'>
          {incompleteCount === 1 ? 'One line needs' : `${incompleteCount} lines need`} a role and
          some time. A role nobody has named cannot be staffed, and a line booking no hours cannot
          be scheduled against.
        </p>
      )}

      {upsert.isError && (
        <p className='text-sm text-destructive'>
          {upsert.error instanceof Error ? upsert.error.message : 'Could not save staffing.'}
        </p>
      )}

      {rows.length === 0 ? (
        <p className='rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-muted-foreground'>
          No staffing added yet — click “Add staffing line” to say who this process needs.
        </p>
      ) : (
        <>
        <div className='overflow-x-auto rounded-xl border border-gray-200'>
          <Table>
            <TableHeader>
              <TableRow>
                {cols.visible.map(({ def }) => (
                  <ResizableTableHead
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={STAFFING_HEAD_CLASSES[def.id]}
                    resizable={def.id !== 'actions'}
                  >
                    {def.header}
                  </ResizableTableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, i) => {
                const name = row.roleLabel.trim() || `line ${i + 1}`
                return (
                  <TableRow key={i} className={cn(isIncomplete(row) && 'bg-amber-50/50')}>
                    <ColumnCells
                      columns={cols}
                      cells={{
                        role: (
                          <TableCell>
                            <RolePicker
                              value={row.roleLabel}
                              custom={customRows.has(i) || isCustomRole(row.roleLabel)}
                              rowLabel={name}
                              onChange={(role) => patchRow(i, { roleLabel: role })}
                              onCustomChange={(custom) => setCustomFor(i, custom)}
                            />
                          </TableCell>
                        ),
                        prep: (
                          <TableCell className='text-right'>
                            <CellInput
                              label={`Prep hours for ${name}`}
                              step='0.25'
                              value={row.prepHrs}
                              onChange={(v) => patchRow(i, { prepHrs: v })}
                            />
                          </TableCell>
                        ),
                        setup: (
                          <TableCell className='text-right'>
                            <CellInput
                              label={`Setup hours for ${name}`}
                              step='0.25'
                              value={row.setupHrs}
                              onChange={(v) => patchRow(i, { setupHrs: v })}
                            />
                          </TableCell>
                        ),
                        run: (
                          <TableCell className='text-right'>
                            <CellInput
                              label={`Run hours for ${name}`}
                              step='0.25'
                              value={row.runHrs}
                              onChange={(v) => patchRow(i, { runHrs: v })}
                            />
                          </TableCell>
                        ),
                        unload: (
                          <TableCell className='text-right'>
                            <CellInput
                              label={`Unload hours for ${name}`}
                              step='0.25'
                              value={row.unloadHrs}
                              onChange={(v) => patchRow(i, { unloadHrs: v })}
                            />
                          </TableCell>
                        ),
                        resources: (
                          <TableCell className='text-right'>
                            <CellInput
                              label={`Resources for ${name}`}
                              min={1}
                              step='1'
                              value={row.numResources}
                              onChange={(v) => patchRow(i, { numResources: v })}
                            />
                          </TableCell>
                        ),
                        scheduleDays: (
                          <TableCell>
                            <ScheduleDaysPicker
                              value={row.scheduleDays}
                              rowLabel={name}
                              onChange={(days) => patchRow(i, { scheduleDays: days })}
                            />
                          </TableCell>
                        ),
                        actions: (
                          <TableCell>
                            <button
                              type='button'
                              aria-label={`Remove ${name}`}
                              title='Remove staffing line'
                              onClick={() => removeRow(i)}
                              className='flex h-8 w-8 items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600 transition-colors hover:bg-red-600 hover:text-white'
                            >
                              <X className='h-4 w-4' strokeWidth={2.5} />
                            </button>
                          </TableCell>
                        ),
                      }}
                    />
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
        </>
      )}

      {/* Person-time is not equipment-time — the slide is explicit about it, and
          two operators on a one-hour run is two person-hours. Stating the total
          here means the author does not have to work that out from the columns. */}
      {rows.length > 0 && (
        <p className='text-xs text-muted-foreground'>
          Hours are per run, per person. {personHours} person-hours/run across {rows.length}{' '}
          {rows.length === 1 ? 'line' : 'lines'}.
        </p>
      )}
    </div>
  )
}
